import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import { useAuthStore } from "@/stores/authStore";
import type { VueIntegrationOptions } from "@sentry/vue";
import type { Router } from "vue-router";

let isLoaded = false;

// Tracks error instances already sent to Sentry so callers further up the
// chain (e.g. store actions) don't report the same GraphQL failure twice.
const reportedErrors = new WeakSet<object>();

export async function sentryException(
  err: unknown,
  extra: Record<string, unknown> = {},
) {
  // We should show the error on development environment
  if (import.meta.env.DEV) {
    // oxlint-disable-next-line no-console
    console.error(err);
  }

  // If the Sentry is not inited, we shouldn't do anything
  if (isLoaded === false) {
    return;
  }

  if (typeof err === "object" && err !== null) {
    if (reportedErrors.has(err)) {
      return;
    }
    reportedErrors.add(err);
  }

  const Sentry = await import("@sentry/vue");

  const data: Record<string, unknown> = {
    ...extra,
  };

  const auth = useAuthStore();

  if (auth) {
    data.userId = auth.id;
  }

  if (err instanceof SentryGraphQLError) {
    const extras = err.toSentryExtras();

    Sentry.withScope((scope) => {
      scope.setTag("graphql.operation", err.operation);
      scope.setFingerprint(["graphql", err.operation]);

      for (const key in data) {
        scope.setExtra(key, data[key]);
      }

      for (const key in extras) {
        scope.setExtra(key, extras[key]);
      }

      Sentry.captureException(err);
    });

    return;
  }

  Sentry.withScope((scope) => {
    if (data) {
      for (const key in data) {
        scope.setExtra(key, data[key]);
      }
    }

    Sentry.captureException(err);
  });
}

export const sentryInit = async (
  app: VueIntegrationOptions["Vue"],
  router: Router,
) => {
  if (import.meta.env.VITE_APP_SENTRY_ENV !== "production") {
    return;
  }

  // Loading the Sentry script dynamically. Because the user should consent
  // for Sentry Analytics cookies.
  const Sentry = await import("@sentry/vue");
  isLoaded = true;

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    maxBreadcrumbs: 50,
    debug: false,
    release: import.meta.env.VITE_APP_SENTRY_RELEASE || "vue-manager@unknown",
    environment: import.meta.env.VITE_APP_SENTRY_ENV || "development",
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.vueIntegration({ tracingOptions: { trackComponents: true } }),
    ],

    tracePropagationTargets: [
      "/^https://ibgmanager.dk/]",
      "ibg.test",
      "ibgmanager.test",
      "api.ibg.test",
      /^https:\/\/ibgmanager\.dk/,
      /^https:\/\/ibg\.proreact\.dk/,
      /^https:\/\/api\.ibgmanager\.dk/,
      /^https:\/\/staging\.ibgmanager\.dk/,
      /^https:\/\/api\.staging\.ibgmanager\.dk/,
      /^\//,
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.02,
    attachProps: true,
    attachStacktrace: true,
    initialScope: {
      tags: {
        sentyr_env: import.meta.env.VITE_APP_SENTRY_ENV || "development",
      },
    },
  });
};
